import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, addHours } from "date-fns";
import { Music, Sparkles, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { formatMoney } from "@/lib/format";

const API = process.env.REACT_APP_BACKEND_URL;
const DURATIONS = [60, 90, 120, 180, 240];
const DEFAULT_EMBED_THEME = {
    primary_color: "#0F766E",
    accent_color: "#14B8A6",
    background_color: "#F8FAFC",
    text_color: "#172033",
    card_color: "#FFFFFF",
};
const validThemeColor = (value) => /^#[0-9a-fA-F]{6}$/.test(String(value || ""));

function readPreviewTheme(value) {
    if (!value) return {};
    try {
        const parsed = JSON.parse(value);
        return Object.fromEntries(Object.entries(parsed).filter(([field, color]) => Object.hasOwn(DEFAULT_EMBED_THEME, field) && validThemeColor(color)));
    } catch {
        return {};
    }
}

// Very small stateless client that always attaches the key + parent_origin as query params.
const call = async (path, method = "get", body) => {
    const url = new URL(`${API}${path}`);
    const qs = new URLSearchParams(window.location.search);
    const key = qs.get("key");
    const parentOrigin = qs.get("parent_origin") || (document.referrer ? new URL(document.referrer).origin : "");
    if (key) url.searchParams.set("key", key);
    if (parentOrigin) url.searchParams.set("parent_origin", parentOrigin);
    const cfg = { method, url: url.toString(), withCredentials: false };
    if (body !== undefined) cfg.data = body;
    const r = await axios(cfg);
    return r.data;
};

function Stepper({ step, total, theme }) {
    return (
        <div className="flex items-center gap-1">
            {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
                <div key={n} className="h-1 flex-1 rounded-full" style={{ backgroundColor: step >= n ? theme.primary_color : `${theme.primary_color}22` }} />
            ))}
        </div>
    );
}

export default function EmbedBookPage() {
    const [params] = useSearchParams();
    const key = params.get("key");
    const previewTheme = useMemo(() => readPreviewTheme(params.get("preview_theme")), [params]);
    const [bootstrap, setBootstrap] = useState(null);
    const [bootError, setBootError] = useState(null);
    const [step, setStep] = useState(1);
    const [serviceId, setServiceId] = useState("");
    const [locationId, setLocationId] = useState("");
    const [roomId, setRoomId] = useState("");
    const [engineerId, setEngineerId] = useState("");
    const [duration, setDuration] = useState(120);
    const [startDate, setStartDate] = useState(format(addHours(new Date(), 24), "yyyy-MM-dd"));
    const [slots, setSlots] = useState([]);
    const [selectedSlotKey, setSelectedSlotKey] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [notes, setNotes] = useState("");
    const [availability, setAvailability] = useState(null);
    const [rankedEngineers, setRankedEngineers] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);
    const [idempotencyKey] = useState(() => `embed-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const rootRef = useRef(null);
    const theme = useMemo(() => ({ ...DEFAULT_EMBED_THEME, ...(bootstrap?.embed?.branding || {}), ...previewTheme }), [bootstrap, previewTheme]);
    const rootStyle = { backgroundColor: theme.background_color, color: theme.text_color };
    const cardStyle = { backgroundColor: theme.card_color, borderColor: `${theme.primary_color}55`, color: theme.text_color };
    const primaryButtonStyle = { backgroundColor: theme.primary_color, borderColor: theme.primary_color, color: "#FFFFFF" };

    // Post iframe height to parent for auto-resize
    useEffect(() => {
        const ro = new ResizeObserver(() => {
            if (!rootRef.current) return;
            const h = rootRef.current.getBoundingClientRect().height;
            try {
                window.parent?.postMessage({ __musook: "resize", height: Math.ceil(h) }, "*");
            } catch (e) { /* ignore */ }
        });
        if (rootRef.current) ro.observe(rootRef.current);
        return () => ro.disconnect();
    }, []);

    // Bootstrap
    useEffect(() => {
        if (!key) { setBootError("Missing embed key"); return; }
        call("/api/embed/bootstrap").then((d) => {
            setBootstrap(d);
            if (d.services?.length) setServiceId(d.services[0].id);
            if (d.locations?.length) setLocationId(d.locations[0].id);
            try { window.parent?.postMessage({ __musook: "ready" }, "*"); } catch (e) { /* ignore */ }
        }).catch((e) => {
            setBootError(e?.response?.data?.detail || e.message);
            try { window.parent?.postMessage({ __musook: "ready" }, "*"); } catch (er) { /* ignore */ }
        });
    }, [key]);

    const service = useMemo(() => bootstrap?.services?.find((s) => s.id === serviceId), [bootstrap, serviceId]);
    const location = useMemo(() => bootstrap?.locations?.find((l) => l.id === locationId), [bootstrap, locationId]);
    const compatibleRooms = useMemo(() => {
        if (!bootstrap || !service) return [];
        return bootstrap.rooms.filter((r) => (!r.supported_services?.length || r.supported_services.includes(service.type)) && (!r.location_id || r.location_id === locationId));
    }, [bootstrap, service, locationId]);

    useEffect(() => {
        if (service && !service.requires_engineer) setEngineerId("");
    }, [service]);

    useEffect(() => {
        if (!bootstrap || !serviceId || !locationId || !startDate) return;
        let cancelled = false;
        call("/api/embed/slots", "post", {
            service_id: serviceId,
            location_id: locationId,
            start_date: startDate,
            end_date: startDate,
            duration_min: duration,
            step_min: 30,
            limit: 100,
        }).then((data) => {
            if (cancelled) return;
            const next = data.slots || [];
            setSlots(next);
            setSelectedSlotKey((prev) => next.some((slot) => `${slot.start_utc}|${slot.room_id}|${slot.engineer?.id || ""}` === prev) ? prev : "");
        }).catch(() => {
            if (!cancelled) { setSlots([]); setSelectedSlotKey(""); }
        });
        return () => { cancelled = true; };
    }, [bootstrap, serviceId, locationId, startDate, duration]);

    const selectedSlot = useMemo(() => slots.find((slot) => `${slot.start_utc}|${slot.room_id}|${slot.engineer?.id || ""}` === selectedSlotKey), [slots, selectedSlotKey]);

    useEffect(() => {
        setRoomId(selectedSlot?.room_id || "");
        setEngineerId(selectedSlot?.engineer?.id || "");
    }, [selectedSlot]);

    const startIso = selectedSlot?.start_utc || null;
    const endIso = selectedSlot?.end_utc || null;

    // Final preview uses the same resolver as slot generation and booking confirmation.
    useEffect(() => {
        if (!bootstrap || !serviceId || !roomId || !startIso || !endIso) {
            setAvailability(null);
            return;
        }
        call("/api/embed/availability", "post", {
            service_id: serviceId, room_id: roomId, start_utc: startIso, end_utc: endIso, engineer_id: engineerId || undefined,
        }).then(setAvailability).catch(() => setAvailability(null));
    }, [bootstrap, serviceId, roomId, startIso, endIso, engineerId]);

    const price = useMemo(() => {
        if (!service) return 0;
        if (service.pricing_model === "hourly") return Math.round(service.price * (duration / 60));
        return service.price;
    }, [service, duration]);
    const depositRequired = useMemo(() => Math.round(price * (service?.deposit_pct || 0.5)), [price, service]);

    const submit = async () => {
        if (!name.trim() || !email.trim()) { setErr("Name and email are required."); return; }
        if (service?.requires_engineer && !engineerId) { setErr("Please pick an engineer."); return; }
        setSubmitting(true); setErr(null);
        try {
            const res = await call("/api/embed/book", "post", {
                name, email, phone, notes,
                room_id: roomId, service_id: serviceId, engineer_id: engineerId || undefined,
                start_utc: startIso, end_utc: endIso, idempotency_key: idempotencyKey,
            });
            setResult(res);
            try {
                window.parent?.postMessage({ __musook: "booking_created", payload: { booking_id: res.booking.id, deposit: res.invoice.deposit_required } }, "*");
            } catch (e) { /* ignore */ }
        } catch (e) {
            const detail = e?.response?.data?.detail || e.message;
            const suggestions = e?.response?.data?.suggestions || [];
            setErr(detail + (suggestions.length ? " — alternatives available." : ""));
        } finally {
            setSubmitting(false);
        }
    };

    if (bootError) {
        return (
            <div className="min-h-[400px] flex items-center justify-center p-6 bg-[hsl(var(--paper))]">
                <Card className="p-6 max-w-md w-full border-border">
                    <div className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" /><div><div className="font-medium">This booking widget can’t load.</div><div className="text-sm text-muted-foreground mt-1">{bootError}</div><div className="text-xs text-muted-foreground mt-2">If you’re the studio, check that this domain is on the embed key’s allow list.</div></div></div>
                </Card>
            </div>
        );
    }

    if (!bootstrap) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    // Success screen
    if (result) {
        const { booking, invoice, payment } = result;
        return (
            <div ref={rootRef} className="min-h-[400px] p-6" style={rootStyle}>
                <Header studio={bootstrap.studio} theme={theme} />
                <Card className="p-6 max-w-2xl mx-auto border-border" style={cardStyle} data-testid="embed-success">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 className="h-6 w-6" /></div>
                        <div>
                            <h2 className="font-display text-2xl font-semibold">Reservation received</h2>
                            <div className="text-sm text-muted-foreground">Booking {booking.id}</div>
                        </div>
                    </div>
                    <div className="mt-4 grid gap-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Session</span><span>{format(new Date(booking.start_utc), "EEE MMM d, h:mm a")} UTC</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="tabular-nums">{formatMoney(invoice.total)}</span></div>
                        <div className="flex justify-between font-medium"><span>Deposit due</span><span className="tabular-nums">{formatMoney(invoice.deposit_required)}</span></div>
                    </div>
                    <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <div className="font-medium">Next step</div>
                        <div className="mt-1">{payment.instructions}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-4">A confirmation has been logged for the studio. Please check your inbox for the deposit link.</div>
                </Card>
            </div>
        );
    }

    // Main flow — 3 steps: 1 pick session, 2 your details, 3 review & reserve
    return (
        <div ref={rootRef} className="min-h-[400px] p-4 md:p-6" style={rootStyle}>
            <Header studio={bootstrap.studio} theme={theme} />
            <div className="max-w-2xl mx-auto">
                <Stepper step={step} total={3} theme={theme} />
                <Card className="mt-4 border-border" style={cardStyle}>
                    {step === 1 && (
                        <div className="p-4 space-y-4">
                            <div>
                                <Label>Service</Label>
                                <Select value={serviceId} onValueChange={setServiceId}>
                                    <SelectTrigger data-testid="embed-service-select"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                                    <SelectContent>
                                        {bootstrap.services.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>{s.name} · {s.pricing_model === "hourly" ? `${formatMoney(s.price)}/hr` : formatMoney(s.price)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Location</Label>
                                <Select value={locationId} onValueChange={(value) => { setLocationId(value); setSelectedSlotKey(""); }}>
                                    <SelectTrigger data-testid="embed-location-select"><SelectValue placeholder="Choose a location" /></SelectTrigger>
                                    <SelectContent>
                                        {(bootstrap.locations || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name} · {l.timezone}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Date</Label>
                                <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setSelectedSlotKey(""); }} data-testid="embed-date" />
                            </div>
                            <div>
                                <Label>Duration</Label>
                                <Select value={String(duration)} onValueChange={(v) => { setDuration(Number(v)); setSelectedSlotKey(""); }}>
                                    <SelectTrigger data-testid="embed-duration"><SelectValue /></SelectTrigger>
                                    <SelectContent>{DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Available time</Label>
                                <Select value={selectedSlotKey} onValueChange={setSelectedSlotKey}>
                                    <SelectTrigger data-testid="embed-slot-select"><SelectValue placeholder={slots.length ? "Choose a confirmed slot" : "No confirmed slots available"} /></SelectTrigger>
                                    <SelectContent>
                                        {slots.map((slot) => {
                                            const key = `${slot.start_utc}|${slot.room_id}|${slot.engineer?.id || ""}`;
                                            return <SelectItem key={key} value={key}>{format(new Date(slot.start_utc), "h:mm a")} · {slot.room_name}{slot.engineer?.name ? ` · ${slot.engineer.name}` : ""}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                                <div className="mt-1 text-xs text-muted-foreground">Times are shown in {location?.timezone || "the studio timezone"}.</div>
                            </div>
                            {selectedSlot && (
                                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                                    Confirmed by Musook availability: {selectedSlot.room_name}{selectedSlot.engineer?.name ? ` with ${selectedSlot.engineer.name}` : ""}.
                                </div>
                            )}
                            {availability && (
                                <div className={`rounded-md border p-3 text-xs ${availability.available ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="embed-availability">
                                    {availability.available ? "✅ This slot is available." : "⚠️ This slot is no longer available. Please choose another."}
                                </div>
                            )}
                            <Button className="w-full" style={primaryButtonStyle} onClick={() => setStep(2)} disabled={!serviceId || !locationId || !selectedSlot || availability?.available === false} data-testid="embed-step1-next">Continue <ArrowRight className="ml-1 h-4 w-4" /></Button>
                        </div>
                    )}
                    {step === 2 && (
                        <div className="p-4 space-y-3">
                            <div>
                                <Label>Your name</Label>
                                <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="embed-name" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="embed-email" />
                            </div>
                            <div>
                                <Label>Phone</Label>
                                <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1…" data-testid="embed-phone" />
                            </div>
                            <div>
                                <Label>Notes (optional)</Label>
                                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the engineer should know?" data-testid="embed-notes" />
                            </div>
                            <div className="flex justify-between">
                                <Button variant="ghost" onClick={() => setStep(1)} data-testid="embed-step2-back">Back</Button>
                                <Button onClick={() => setStep(3)} style={primaryButtonStyle} disabled={!name.trim() || !email.trim()} data-testid="embed-step2-next">Continue <ArrowRight className="ml-1 h-4 w-4" /></Button>
                            </div>
                        </div>
                    )}
                    {step === 3 && (
                        <div className="p-4 space-y-4">
                            <div>
                                <h3 className="font-display text-lg font-semibold">Confirm your reservation</h3>
                                <p className="text-xs text-muted-foreground">A deposit will secure the slot. The studio will email you the payment link.</p>
                            </div>
                            <div className="grid gap-2 text-sm rounded-md border border-border bg-muted/40 p-3">
                                <div className="flex justify-between"><span className="text-muted-foreground">Service</span><span>{service?.name}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Room</span><span>{compatibleRooms.find((r) => r.id === roomId)?.name}</span></div>
                                {engineerId && <div className="flex justify-between"><span className="text-muted-foreground">Engineer</span><span>{rankedEngineers.find((e) => e.id === engineerId)?.name}</span></div>}
                                <div className="flex justify-between"><span className="text-muted-foreground">When</span><span>{format(new Date(startIso), "EEE, MMM d yyyy · h:mm a")} UTC ({duration} min)</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="tabular-nums">{formatMoney(price)}</span></div>
                                <div className="flex justify-between font-medium"><span>Deposit</span><span className="tabular-nums">{formatMoney(depositRequired)}</span></div>
                            </div>
                            {err && <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive text-sm p-2" data-testid="embed-error">{err}</div>}
                            <div className="flex justify-between">
                                <Button variant="ghost" onClick={() => setStep(2)} disabled={submitting} data-testid="embed-step3-back">Back</Button>
                                <Button onClick={submit} style={primaryButtonStyle} disabled={submitting} data-testid="embed-submit">{submitting ? "Reserving…" : `Reserve & Pay ${formatMoney(depositRequired)}`}</Button>
                            </div>
                        </div>
                    )}
                </Card>
                <div className="text-center text-[11px] text-muted-foreground mt-3">Powered by <span className="font-medium">Musook</span></div>
            </div>
        </div>
    );
}

function Header({ studio, theme }) {
    return (
        <div className="max-w-2xl mx-auto mb-4 flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: theme.primary_color }}><Music className="h-5 w-5" strokeWidth={1.75} /></div>
            <div>
                <div className="font-display text-lg font-semibold leading-tight">{studio?.name || "Musook"}</div>
                <div className="text-[11px] opacity-70">{studio?.tagline}</div>
            </div>
            <div className="ml-auto text-[11px] opacity-70 flex items-center gap-1"><Sparkles className="h-3 w-3" style={{ color: theme.accent_color }} /> Live availability</div>
        </div>
    );
}